import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Heart, Plus, Trash2, ShoppingBag, Edit2, X, Check, Coffee, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { useFavorites } from '@/context/FavoritesContext';
import { toast } from 'sonner';

export default function FavoritesPage() {
  const { isAuthenticated } = useAuth();
  const { favorites, loading, removeFavorite, updateFavorite } = useFavorites();
  const { addToCart } = useCart();
  const [editingNickname, setEditingNickname] = useState(null);
  const [tempNickname, setTempNickname] = useState('');

  const handleAddToCart = (fav) => {
    // Build the item object for cart
    const item = {
      id: fav.item_id,
      name: fav.item_name,
      image: fav.item_image,
      price: fav.current_price,
      sizes: fav.selected_size ? [{ name: fav.selected_size, price: fav.current_price }] : null
    };

    // addToCart signature: (item, selectedSize, modifiers, specialNotes, removedIngredients, extraIngredients, addOns)
    addToCart(
      item,
      fav.selected_size,
      fav.modifiers,
      fav.special_instructions || '',
      fav.removed_ingredients || null,  // removedIngredients
      fav.extras || null,  // extraIngredients (extras saved as extraIngredients)
      null  // addOns
    );

    toast.success(`${fav.nickname || fav.item_name} added to cart!`);
  };

  const handleSaveNickname = async (favId) => {
    await updateFavorite(favId, { nickname: tempNickname });
    setEditingNickname(null);
  };

  const getItemDescription = (fav) => {
    const parts = [];
    if (fav.selected_size) parts.push(fav.selected_size);
    if (fav.modifiers?.length > 0) {
      fav.modifiers.forEach(m => parts.push(m.selected_option));
    }
    if (fav.extras?.length > 0) {
      fav.extras.forEach(e => parts.push(`+${e.name || e}`));
    }
    if (fav.removed_ingredients?.length > 0) {
      fav.removed_ingredients.forEach(r => parts.push(`No ${r}`));
    }
    return parts.join(' • ');
  };

  if (!isAuthenticated) {
    return (
      <div className="pt-24 pb-16 min-h-screen" data-testid="favorites-page">
        <section className="py-24">
          <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
            <Heart className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
            <h1 className="font-heading text-4xl mb-4">Save Your Favorites</h1>
            <p className="text-muted-foreground mb-8">
              Log in to save your customized drink orders and reorder them with one tap!
            </p>
            <Link to="/menu">
              <Button className="rounded-full bg-primary hover:bg-primary/90">
                Browse Menu
              </Button>
            </Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-16 min-h-screen" data-testid="favorites-page">
      {/* Hero */}
      <section className="py-8 bg-gradient-to-b from-secondary/50 to-transparent">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-heading text-4xl md:text-5xl text-foreground mb-2"
          >
            Your Favorites
          </motion.h1>
          <p className="text-muted-foreground">
            Your saved customized orders — ready to reorder with one tap
          </p>
        </div>
      </section>

      {/* Favorites List */}
      <section className="py-8">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="text-center py-16">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
              <p className="text-muted-foreground mt-4">Loading favorites...</p>
            </div>
          ) : favorites.length === 0 ? (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <Heart className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
              <h2 className="font-heading text-2xl mb-2">No favorites yet</h2>
              <p className="text-muted-foreground mb-6">
                Save your customized drinks from the menu to quickly reorder them!
              </p>
              <Link to="/menu" data-testid="browse-menu-btn">
                <Button className="rounded-full bg-primary hover:bg-primary/90">
                  <Coffee className="w-4 h-4 mr-2" />
                  Browse Menu
                </Button>
              </Link>
            </motion.div>
          ) : (
            <div className="space-y-4">
              {favorites.map((fav, i) => (
                <motion.div
                  key={fav.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className={`bg-card p-4 rounded-2xl shadow-sm border border-border ${
                    !fav.is_available ? 'opacity-60' : ''
                  }`}
                  data-testid={`favorite-item-${fav.id}`}
                >
                  <div className="flex gap-4">
                    <img 
                      src={fav.item_image} 
                      alt={fav.item_name}
                      className="w-24 h-24 rounded-xl object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      {/* Nickname / Name */}
                      {editingNickname === fav.id ? (
                        <div className="flex items-center gap-2 mb-1">
                          <Input
                            value={tempNickname}
                            onChange={(e) => setTempNickname(e.target.value)}
                            placeholder="Give it a nickname..."
                            className="h-8 text-sm max-w-[200px]"
                            data-testid={`nickname-input-${fav.id}`}
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleSaveNickname(fav.id)}
                            data-testid={`save-nickname-${fav.id}`}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingNickname(null)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium text-lg">
                            {fav.nickname || fav.item_name}
                          </h3>
                          <button
                            onClick={() => {
                              setEditingNickname(fav.id);
                              setTempNickname(fav.nickname || '');
                            }}
                            className="text-muted-foreground hover:text-primary"
                            data-testid={`edit-nickname-${fav.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                      
                      {fav.nickname && (
                        <p className="text-sm text-muted-foreground">{fav.item_name}</p>
                      )}
                      
                      {getItemDescription(fav) && (
                        <p className="text-sm text-muted-foreground">
                          {getItemDescription(fav)}
                        </p>
                      )}
                      
                      {fav.special_instructions && (
                        <p className="text-xs text-muted-foreground italic mt-1">
                          Note: {fav.special_instructions}
                        </p>
                      )}
                      
                      <p className="text-primary font-medium mt-1">
                        {fav.current_price?.toFixed(2)}
                      </p>
                      
                      {!fav.is_available && (
                        <span className="text-xs text-destructive">Currently unavailable</span>
                      )}
                    </div>
                    
                    <div className="flex flex-col items-end justify-between">
                      <button
                        onClick={() => removeFavorite(fav.id)}
                        className="w-8 h-8 rounded-full bg-destructive/10 text-destructive flex items-center justify-center hover:bg-destructive/20"
                        data-testid={`remove-favorite-${fav.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      
                      <Button
                        onClick={() => handleAddToCart(fav)}
                        disabled={!fav.is_available}
                        className="rounded-full bg-primary hover:bg-primary/90"
                        data-testid={`add-to-cart-${fav.id}`}
                      >
                        <ShoppingBag className="w-4 h-4 mr-2" />
                        Add to Cart
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
              
              {/* Quick actions */}
              <div className="flex flex-wrap gap-4 mt-8">
                <Link to="/menu">
                  <Button variant="outline" className="rounded-full border-2 border-primary/20 hover:border-primary">
                    <Plus className="w-4 h-4 mr-2" />
                    Add More Favorites
                  </Button>
                </Link>
                <Link to="/order">
                  <Button className="rounded-full bg-primary hover:bg-primary/90">
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    View Cart
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
